import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, Star, TrendingUp, Users, Clock, CheckCircle, 
  ArrowRight, Zap, BarChart3, Target, X, User, Lock,
  ChevronDown, Mail, Phone, MapPin, FileText, Shield,
  Plus, Minus, Trash2, RefreshCw, LogOut, Package, Link2, ExternalLink,
  AlertTriangle, Crown, Brain, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast, Toaster } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import SalesChatBubble from '../components/SalesChatBubble';
import ProUpgradePromo from '../components/ProUpgradePromo';
import UpgradeModal from '../components/UpgradeModal';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

const StoreLanding = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedSubProduct, setSelectedSubProduct] = useState(null);
  
  // Shopping cart state
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Checkout form state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: ''
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  
  // Country list for dropdown
  // Country list with states/provinces
  const countryStates = {
    "United States": ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"],
    "Canada": ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Northwest Territories", "Nunavut", "Yukon"],
    "United Kingdom": ["England", "Scotland", "Wales", "Northern Ireland"],
    "Australia": ["New South Wales", "Victoria", "Queensland", "Western Australia", "South Australia", "Tasmania", "Northern Territory", "Australian Capital Territory"],
    "Germany": ["Baden-Württemberg", "Bavaria", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hesse", "Lower Saxony", "Mecklenburg-Vorpommern", "North Rhine-Westphalia", "Rhineland-Palatinate", "Saarland", "Saxony", "Saxony-Anhalt", "Schleswig-Holstein", "Thuringia"],
    "India": ["Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Delhi"],
    "Brazil": ["Acre", "Alagoas", "Amapá", "Amazonas", "Bahia", "Ceará", "Distrito Federal", "Espírito Santo", "Goiás", "Maranhão", "Mato Grosso", "Mato Grosso do Sul", "Minas Gerais", "Pará", "Paraíba", "Paraná", "Pernambuco", "Piauí", "Rio de Janeiro", "Rio Grande do Norte", "Rio Grande do Sul", "Rondônia", "Roraima", "Santa Catarina", "São Paulo", "Sergipe", "Tocantins"],
    "Mexico": ["Aguascalientes", "Baja California", "Baja California Sur", "Campeche", "Chiapas", "Chihuahua", "Coahuila", "Colima", "Durango", "Guanajuato", "Guerrero", "Hidalgo", "Jalisco", "México", "Mexico City", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca", "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa", "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán", "Zacatecas"],
    "Jamaica": ["Clarendon", "Hanover", "Kingston", "Manchester", "Portland", "St. Andrew", "St. Ann", "St. Catherine", "St. Elizabeth", "St. James", "St. Mary", "St. Thomas", "Trelawny", "Westmoreland"],
    "Nigeria": ["Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara", "FCT Abuja"],
    "South Africa": ["Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo", "Mpumalanga", "North West", "Northern Cape", "Western Cape"],
    "France": ["Auvergne-Rhône-Alpes", "Bourgogne-Franche-Comté", "Brittany", "Centre-Val de Loire", "Corsica", "Grand Est", "Hauts-de-France", "Île-de-France", "Normandy", "Nouvelle-Aquitaine", "Occitanie", "Pays de la Loire", "Provence-Alpes-Côte d'Azur"],
    "Italy": ["Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna", "Friuli Venezia Giulia", "Lazio", "Liguria", "Lombardy", "Marche", "Molise", "Piedmont", "Apulia", "Sardinia", "Sicily", "Tuscany", "Trentino-Alto Adige", "Umbria", "Aosta Valley", "Veneto"],
    "Spain": ["Andalusia", "Aragon", "Asturias", "Balearic Islands", "Basque Country", "Canary Islands", "Cantabria", "Castile and León", "Castilla-La Mancha", "Catalonia", "Extremadura", "Galicia", "La Rioja", "Madrid", "Murcia", "Navarre", "Valencia"],
    "China": ["Anhui", "Beijing", "Chongqing", "Fujian", "Gansu", "Guangdong", "Guangxi", "Guizhou", "Hainan", "Hebei", "Heilongjiang", "Henan", "Hubei", "Hunan", "Inner Mongolia", "Jiangsu", "Jiangxi", "Jilin", "Liaoning", "Ningxia", "Qinghai", "Shaanxi", "Shandong", "Shanghai", "Shanxi", "Sichuan", "Tianjin", "Tibet", "Xinjiang", "Yunnan", "Zhejiang"],
    "Japan": ["Hokkaido", "Aomori", "Iwate", "Miyagi", "Akita", "Yamagata", "Fukushima", "Ibaraki", "Tochigi", "Gunma", "Saitama", "Chiba", "Tokyo", "Kanagawa", "Niigata", "Toyama", "Ishikawa", "Fukui", "Yamanashi", "Nagano", "Gifu", "Shizuoka", "Aichi", "Mie", "Shiga", "Kyoto", "Osaka", "Hyogo", "Nara", "Wakayama", "Tottori", "Shimane", "Okayama", "Hiroshima", "Yamaguchi", "Tokushima", "Kagawa", "Ehime", "Kochi", "Fukuoka", "Saga", "Nagasaki", "Kumamoto", "Oita", "Miyazaki", "Kagoshima", "Okinawa"],
    "UAE": ["Abu Dhabi", "Dubai", "Sharjah", "Ajman", "Umm Al Quwain", "Ras Al Khaimah", "Fujairah"],
    "Philippines": ["Metro Manila", "Cebu", "Davao", "Calabarzon", "Central Luzon", "Western Visayas", "Central Visayas", "Ilocos Region", "Bicol Region", "Eastern Visayas", "Zamboanga Peninsula", "Northern Mindanao", "Soccsksargen", "Caraga", "BARMM", "CAR", "Mimaropa"],
    "Indonesia": ["Aceh", "Bali", "Banten", "Bengkulu", "Central Java", "Central Kalimantan", "Central Sulawesi", "East Java", "East Kalimantan", "East Nusa Tenggara", "Gorontalo", "Jakarta", "Jambi", "Lampung", "Maluku", "North Kalimantan", "North Maluku", "North Sulawesi", "North Sumatra", "Papua", "Riau", "Riau Islands", "South Kalimantan", "South Sulawesi", "South Sumatra", "Southeast Sulawesi", "West Java", "West Kalimantan", "West Nusa Tenggara", "West Papua", "West Sulawesi", "West Sumatra", "Yogyakarta"],
    "Trinidad and Tobago": ["Arima", "Chaguanas", "Couva-Tabaquite-Talparo", "Diego Martin", "Mayaro-Rio Claro", "Penal-Debe", "Point Fortin", "Port of Spain", "Princes Town", "San Fernando", "San Juan-Laventille", "Sangre Grande", "Siparia", "Tobago", "Tunapuna-Piarco"]
  };
  
  const countries = Object.keys(countryStates).concat([
    "Netherlands", "Belgium", "Switzerland", "Austria", "Sweden", "Norway", "Denmark", "Finland",
    "Ireland", "Portugal", "Greece", "Poland", "Czech Republic", "Hungary", "Romania", "Bulgaria",
    "South Korea", "Singapore", "Malaysia", "Thailand", "Vietnam", "Argentina", "Colombia", "Chile", "Peru",
    "Kenya", "Egypt", "Morocco", "Saudi Arabia", "Israel", "Turkey", "Russia", "Ukraine", "New Zealand",
    "Bahamas", "Barbados", "Haiti", "Dominican Republic", "Puerto Rico", "Cuba", "Panama", "Costa Rica",
    "Guatemala", "Honduras", "El Salvador", "Nicaragua", "Ecuador", "Venezuela", "Bolivia", "Paraguay", "Uruguay"
  ]).filter((v, i, a) => a.indexOf(v) === i).sort();

  // Get states for selected country
  const getStatesForCountry = (country) => {
    return countryStates[country] || [];
  };

  // Sign In modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInForm, setSignInForm] = useState({ email: '', password: '' });
  const [signInLoading, setSignInLoading] = useState(false);
  
  // reCAPTCHA state
  const [signInCaptchaToken, setSignInCaptchaToken] = useState(null);
  const signInCaptchaRef = useRef(null);
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  
  // Create Account modal state
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [createAccountForm, setCreateAccountForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [createAccountLoading, setCreateAccountLoading] = useState(false);
  
  // Email verification state
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  
  // Forgot Password state
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordCode, setResetPasswordCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // TradingView username for indicator purchases
  const [tradingViewUsername, setTradingViewUsername] = useState('');
  
  // Promotion banner state
  const [promotion, setPromotion] = useState(null);
  const [promoCountdown, setPromoCountdown] = useState(null);
  
  // PRO Upgrade Promo state
  const [showProPromo, setShowProPromo] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Terms agreement checkbox
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  
  // Order success modal
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [orderEmail, setOrderEmail] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);
  
  // My Services modal
  const [showMyServicesModal, setShowMyServicesModal] = useState(false);
  
  // Multi-step checkout state
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: Account, 2: Billing, 3: Payment
  const [checkoutAccountForm, setCheckoutAccountForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mailingAddress: '',
    mailingCity: '',
    mailingState: '',
    mailingZipCode: '',
    mailingCountry: '',
    password: '',
    confirmPassword: ''
  });
  const [checkoutVerificationCode, setCheckoutVerificationCode] = useState('');
  const [checkoutVerificationSent, setCheckoutVerificationSent] = useState(false);
  const [checkoutVerified, setCheckoutVerified] = useState(false);
  const [checkoutAccountCreated, setCheckoutAccountCreated] = useState(false);
  const [sendingCheckoutVerification, setSendingCheckoutVerification] = useState(false);
  const [verifyingCheckoutCode, setVerifyingCheckoutCode] = useState(false);
  const [creatingCheckoutAccount, setCreatingCheckoutAccount] = useState(false);

  // Site content (editable by admin)
  const [siteContent, setSiteContent] = useState({
    aboutUs: {
      title: "About FORTHEWISEPRO LLC",
      content: "FORTHEWISEPRO LLC is a trading education platform dedicated to skills development. We provide educational tools, custom TradingView indicators, and personalized mentorship programs to help traders of all levels improve their knowledge and skills."
    },
    contactUs: {
      title: "Contact Us",
      email: "worood@forthewisepro.com",
      phone: "+1 (876) 361-8787",
      address: "Kingston, Jamaica"
    },
    serviceTerms: "By using our services, you agree to our terms of service. All products are for educational purposes only and do not constitute financial advice. Trading involves risk and you should only trade with capital you can afford to allocate to trading activities.",
    refundPolicy: "Digital products are non-refundable once accessed. Refund requests must be submitted within 24 hours of purchase for products not yet accessed."
  });
  
  // Modal states for policies
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  
  // Product ratings state
  const [productRatings, setProductRatings] = useState({});
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [selectedProductReviews, setSelectedProductReviews] = useState(null);

  useEffect(() => {
    fetchProducts();
    checkAuth();
    loadCart();
    fetchSiteContent();
    restoreCheckoutState();
    fetchPromotion();
  }, []);
  
  // Fetch product ratings when products are loaded
  useEffect(() => {
    if (products.length > 0) {
      fetchProductRatings();
    }
  }, [products]);
  
  // Restore checkout state from localStorage (for Square redirect back)
  const restoreCheckoutState = () => {
    const savedCheckoutState = localStorage.getItem('checkout_state');
    if (savedCheckoutState) {
      try {
        const state = JSON.parse(savedCheckoutState);
        if (state.checkoutAccountCreated) {
          setCheckoutAccountCreated(true);
          setCheckoutVerified(true);
          setCheckoutStep(state.checkoutStep || 2);
          setCheckoutAccountForm(state.checkoutAccountForm || {});
          setCheckoutForm(state.checkoutForm || {});
          setTradingViewUsername(state.tradingViewUsername || '');
          setAgreedToTerms(state.agreedToTerms || false);
          // If returning from Square with pending order, show checkout
          const pendingOrder = localStorage.getItem('pending_order');
          if (pendingOrder) {
            setShowCheckout(true);
            setCheckoutStep(3); // Payment confirmation step
          }
        }
      } catch (e) {
        console.error('Error restoring checkout state:', e);
      }
    }
  };
  
  // Save checkout state to localStorage
  const saveCheckoutState = (additionalState = {}) => {
    const state = {
      checkoutAccountCreated,
      checkoutVerified,
      checkoutStep,
      checkoutAccountForm,
      checkoutForm,
      tradingViewUsername,
      agreedToTerms,
      ...additionalState
    };
    localStorage.setItem('checkout_state', JSON.stringify(state));
  };
  
  // Clear checkout state
  const clearCheckoutState = () => {
    localStorage.removeItem('checkout_state');
    localStorage.removeItem('pending_order');
    setCheckoutStep(1);
    setCheckoutAccountCreated(false);
    setCheckoutVerified(false);
    setCheckoutVerificationSent(false);
    setCheckoutAccountForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      mailingAddress: '',
      mailingCity: '',
      mailingState: '',
      mailingZipCode: '',
      mailingCountry: '',
      password: '',
      confirmPassword: ''
    });
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('guestToken');
    if (token) {
      try {
        const res = await fetch(`${API}/guest/verify?token=${token}`);
        const data = await res.json();
        if (data.success && data.user) {
          setUser(data.user);
        }
      } catch (err) {
        console.error('Auth check error:', err);
      }
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${API}/products`);
      const data = await res.json();
      if (data.success) {
        setProducts(data.products);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSiteContent = async () => {
    try {
      const res = await fetch(`${API}/site-content`);
      const data = await res.json();
      if (data.success && data.content) {
        setSiteContent(data.content);
      }
    } catch (err) {
      // Use default content if fetch fails
      console.log('Using default site content');
    }
  };
  
  // Fetch promotion banner
  const fetchPromotion = async () => {
    try {
      const response = await fetch(`${API}/promotion`);
      const data = await response.json();
      if (data.success) {
        setPromotion(data);
      }
    } catch (err) {
      // Ignore promotion fetch errors
    }
  };
  
  // Countdown timer for flash sales
  useEffect(() => {
    if (!promotion?.end_time) {
      setPromoCountdown(null);
      return;
    }

    const endTime = new Date(promotion.end_time).getTime();
    
    const updateCountdown = () => {
      const now = Date.now();
      const diff = endTime - now;
      
      if (diff <= 0) {
        setPromoCountdown({ expired: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setPromoCountdown({ days, hours, minutes, seconds, expired: false });
    };
    
    // Initial update
    updateCountdown();
    
    // Update every second
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [promotion?.end_time]);
  
  // Copy discount code to clipboard
  const copyDiscountCode = () => {
    if (promotion?.discount_code) {
      navigator.clipboard.writeText(promotion.discount_code);
      toast.success('Discount code copied!');
    }
  };
  
  // Fetch product ratings from approved reviews
  const fetchProductRatings = async () => {
    try {
      // Fetch ratings for bonbot-signal-generator (the main product with reviews)
      const res = await fetch(`${API}/public/product-rating/bonbot-signal-generator`);
      const data = await res.json();
      if (data.success) {
        setProductRatings(prev => ({
          ...prev,
          'bonbot-signal-generator': {
            average: data.average_rating,
            count: data.total_reviews,
            distribution: data.rating_distribution,
            reviews: data.reviews
          }
        }));
      }
    } catch (err) {
      console.log('Failed to fetch product ratings');
    }
  };
  
  // Show reviews modal for a product
  const handleShowReviews = (productId) => {
    const ratingData = productRatings[productId];
    if (ratingData && ratingData.count > 0) {
      setSelectedProductReviews({
        productId,
        ...ratingData
      });
      setShowReviewsModal(true);
    }
  };
  
  // Checkout account creation functions
  const handleSendCheckoutVerification = async () => {
    if (!checkoutAccountForm.email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSendingCheckoutVerification(true);
    try {
      const res = await fetch(`${API}/checkout/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: checkoutAccountForm.email })
      });
      const data = await res.json();
      
      if (data.success) {
        setCheckoutVerificationSent(true);
        toast.success('Verification code sent to your email!');
        // For dev mode, show the code
        if (data.debug_code) {
          console.log('Dev mode verification code:', data.debug_code);
        }
      } else {
        toast.error(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setSendingCheckoutVerification(false);
    }
  };
  
  const handleVerifyCheckoutCode = async () => {
    if (!checkoutVerificationCode || checkoutVerificationCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    
    setVerifyingCheckoutCode(true);
    try {
      const res = await fetch(`${API}/checkout/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: checkoutAccountForm.email,
          code: checkoutVerificationCode
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setCheckoutVerified(true);
        toast.success('Email verified! Now create your password.');
      } else {
        toast.error(data.message || 'Invalid verification code');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setVerifyingCheckoutCode(false);
    }
  };
  
  const handleCreateCheckoutAccount = async () => {
    // Validate all fields
    if (!checkoutAccountForm.firstName || !checkoutAccountForm.lastName) {
      toast.error('Please enter your full name');
      return;
    }
    if (!checkoutAccountForm.email) {
      toast.error('Please enter your email');
      return;
    }
    if (!checkoutAccountForm.mailingAddress || !checkoutAccountForm.mailingCity || 
        !checkoutAccountForm.mailingZipCode || !checkoutAccountForm.mailingCountry) {
      toast.error('Please complete your mailing address');
      return;
    }
    if (!checkoutAccountForm.password || checkoutAccountForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (checkoutAccountForm.password !== checkoutAccountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    // Password strength check
    const hasUppercase = /[A-Z]/.test(checkoutAccountForm.password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(checkoutAccountForm.password);
    if (!hasUppercase || !hasSpecial) {
      toast.error('Password must contain at least one uppercase letter and one special character');
      return;
    }
    
    setCreatingCheckoutAccount(true);
    try {
      const res = await fetch(`${API}/checkout/create-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: checkoutAccountForm.email,
          password: checkoutAccountForm.password,
          first_name: checkoutAccountForm.firstName,
          last_name: checkoutAccountForm.lastName,
          phone: checkoutAccountForm.phone,
          mailing_address: {
            street: checkoutAccountForm.mailingAddress,
            city: checkoutAccountForm.mailingCity,
            state: checkoutAccountForm.mailingState,
            zip_code: checkoutAccountForm.mailingZipCode,
            country: checkoutAccountForm.mailingCountry
          }
        })
      });
      const data = await res.json();
      
      if (data.success) {
        setCheckoutAccountCreated(true);
        // Auto-fill billing info from mailing address
        setCheckoutForm({
          fullName: `${checkoutAccountForm.firstName} ${checkoutAccountForm.lastName}`,
          email: checkoutAccountForm.email,
          phone: checkoutAccountForm.phone,
          address: checkoutAccountForm.mailingAddress,
          city: checkoutAccountForm.mailingCity,
          state: checkoutAccountForm.mailingState,
          zipCode: checkoutAccountForm.mailingZipCode,
          country: checkoutAccountForm.mailingCountry
        });
        // Save state and move to billing step
        setCheckoutStep(2);
        saveCheckoutState({ checkoutAccountCreated: true, checkoutStep: 2 });
        // Log in the user
        if (data.token) {
          localStorage.setItem('guestToken', data.token);
          setUser(data.user);
        }
        toast.success('Account created! Now enter your billing information.');
      } else {
        toast.error(data.message || 'Failed to create account');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setCreatingCheckoutAccount(false);
    }
  };
  
  // Secret admin access - keyboard shortcut (Ctrl+Shift+A)
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastLogoClick, setLastLogoClick] = useState(0);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        window.location.href = '/admin';
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Secret admin access - click logo 5 times within 3 seconds
  const handleLogoClick = () => {
    const now = Date.now();
    if (now - lastLogoClick > 3000) {
      // Reset if more than 3 seconds since last click
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    setLastLogoClick(now);
    
    if (logoClickCount >= 4) {
      // 5th click (count starts at 0)
      window.location.href = '/admin';
    }
  };
  
  // Cart functions
  const loadCart = () => {
    const savedCart = localStorage.getItem('bonbot_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };
  
  const saveCart = (newCart) => {
    localStorage.setItem('bonbot_cart', JSON.stringify(newCart));
    setCart(newCart);
  };
  
  const addToCart = (product, subProduct = null) => {
    // Get payment link: prioritize sub-product's link, then fall back to product's link
    const paymentLink = subProduct?.payment_link || product.payment_link || '';
    
    const item = {
      id: subProduct ? `${product.product_id}_${subProduct.sub_id}` : product.product_id,
      product_id: product.product_id,
      sub_product_id: subProduct?.sub_id || null,
      name: subProduct ? `${product.name} - ${subProduct.name}` : product.name,
      price: subProduct ? subProduct.price : product.price,
      price_type: subProduct ? subProduct.price_type : product.price_type,
      image_url: product.image_url,
      category: product.category,
      payment_link: paymentLink,
      quantity: 1
    };
    
    const existingIndex = cart.findIndex(i => i.id === item.id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      saveCart(newCart);
      toast.success('Quantity updated in cart!');
    } else {
      saveCart([...cart, item]);
      toast.success('Added to cart!');
    }
  };
  
  const removeFromCart = (itemId) => {
    const newCart = cart.filter(item => item.id !== itemId);
    saveCart(newCart);
    toast.info('Removed from cart');
  };
  
  const updateQuantity = (itemId, delta) => {
    const newCart = cart.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    });
    saveCart(newCart);
  };
  
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setSelectedSubProduct(null);
    setShowProductModal(true);
  };

  const handleSignIn = async (e) => {
    if (e) e.preventDefault();
    
    // Verify captcha
    if (!signInCaptchaToken) {
      toast.error('Please complete the human verification');
      return;
    }
    
    setSignInLoading(true);
    
    try {
      const res = await fetch(`${API}/guest/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signInForm.email,
          password: signInForm.password,
          captcha_token: signInCaptchaToken
        })
      });
      
      const data = await res.json();
      
      if (data.success && data.token) {
        localStorage.setItem('guestToken', data.token);
        toast.success('Welcome back!');
        setShowSignInModal(false);
        setUser(data.user);
        // Reset captcha
        if (signInCaptchaRef.current) {
          signInCaptchaRef.current.reset();
        }
        setSignInCaptchaToken(null);
        // Stay on landing page - show welcome banner
      } else {
        // Check if account is blocked/paused - redirect to plan selection
        if (data.status === 'blocked' || data.status === 'paused' || data.status === 'trial_expired') {
          setShowSignInModal(false);
          toast.error(
            <div>
              <p className="font-bold">⚠️ No Active Plan</p>
              <p className="text-sm">Your account does not have an active subscription.</p>
              <p className="text-sm mt-2">Please select a plan to regain access.</p>
              <p className="text-xs mt-2 text-amber-400">After payment, account activation takes up to 30 minutes.</p>
            </div>,
            { duration: 15000 }
          );
          // Scroll to products section
          setTimeout(() => {
            const productsSection = document.getElementById('products');
            if (productsSection) {
              productsSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
          // Reset captcha
          if (signInCaptchaRef.current) {
            signInCaptchaRef.current.reset();
          }
          setSignInCaptchaToken(null);
          return;
        }
        
        // Check if account is locked due to too many failed attempts
        if (data.status === 'account_locked' || data.locked) {
          toast.error(
            <div>
              <p className="font-bold">🔒 Account Locked</p>
              <p className="text-sm">{data.message}</p>
              <p className="text-xs mt-2 text-blue-400">Click the chat icon below to contact support</p>
            </div>,
            { duration: 10000 }
          );
        } else if (data.remaining_attempts !== undefined) {
          toast.error(
            <div>
              <p>{data.message}</p>
              <p className="text-xs mt-1 text-amber-400">⚠️ {data.remaining_attempts} attempts remaining</p>
            </div>,
            { duration: 5000 }
          );
        } else {
          toast.error(data.message || 'Invalid email or password');
        }
        // Reset captcha on failure
        if (signInCaptchaRef.current) {
          signInCaptchaRef.current.reset();
        }
        setSignInCaptchaToken(null);
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
      // Reset captcha on error
      if (signInCaptchaRef.current) {
        signInCaptchaRef.current.reset();
      }
      setSignInCaptchaToken(null);
    } finally {
      setSignInLoading(false);
    }
  };
  
  // Handle Create Account
  const handleCreateAccount = async (e) => {
    if (e) e.preventDefault();
    
    // Validate passwords match
    if (createAccountForm.password !== createAccountForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Password strength validation
    const password = createAccountForm.password;
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least 1 uppercase letter');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      toast.error('Password must contain at least 1 special character (!@#$%^&*...)');
      return;
    }
    
    setCreateAccountLoading(true);
    
    try {
      const res = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: createAccountForm.email,
          password: createAccountForm.password,
          first_name: createAccountForm.firstName,
          last_name: createAccountForm.lastName
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Account created! Please check your email for verification code.');
        setShowCreateAccountModal(false);
        setVerificationEmail(createAccountForm.email);
        setShowVerificationModal(true);
      } else {
        toast.error(data.message || 'Failed to create account');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setCreateAccountLoading(false);
    }
  };
  
  // Handle Email Verification
  const handleVerifyEmail = async (e) => {
    if (e) e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    
    setVerifyingEmail(true);
    
    try {
      const res = await fetch(`${API}/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: verificationEmail,
          code: verificationCode
        })
      });
      
      const data = await res.json();
      
      if (data.success && data.token) {
        localStorage.setItem('guestToken', data.token);
        toast.success('Email verified! Your account is now active.');
        setShowVerificationModal(false);
        setUser(data.user);
        // Reset form
        setCreateAccountForm({
          email: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: ''
        });
        setVerificationCode('');
      } else {
        toast.error(data.message || 'Verification failed');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setVerifyingEmail(false);
    }
  };
  
  // Handle Forgot Password - Send Reset Code
  const handleForgotPassword = async (e) => {
    if (e) e.preventDefault();
    
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotPasswordLoading(true);
    
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Reset code sent! Check your email.');
        setShowForgotPasswordModal(false);
        setShowResetPasswordModal(true);
      } else {
        toast.error(data.message || 'Failed to send reset code');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };
  
  // Handle Reset Password
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    
    if (resetPasswordCode.length !== 6) {
      toast.error('Please enter the 6-digit reset code');
      return;
    }
    
    // Password strength validation
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Password must contain at least 1 uppercase letter');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      toast.error('Password must contain at least 1 special character (!@#$%^&*...)');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setResettingPassword(true);
    
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          code: resetPasswordCode,
          new_password: newPassword
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Password reset successfully! You can now sign in.');
        setShowResetPasswordModal(false);
        setShowSignInModal(true);
        // Reset form
        setForgotPasswordEmail('');
        setResetPasswordCode('');
        setNewPassword('');
        setConfirmNewPassword('');
      } else {
        toast.error(data.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Connection error. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };
  
  const handleBuyNow = (product, subProduct = null) => {
    addToCart(product, subProduct);
    setShowProductModal(false);
    setShowCart(true);
  };

  // Check if cart has TradingView indicators
  const hasTradingViewIndicators = () => {
    return cart.some(item => item.category === 'indicator');
  };

  // Generate address suggestions based on user input
  const generateAddressSuggestions = (input) => {
    const trimmedInput = input.trim().toLowerCase();
    if (trimmedInput.length < 3) return [];
    
    // Common street types
    const streetTypes = ['Street', 'Avenue', 'Road', 'Boulevard', 'Drive', 'Lane', 'Court', 'Place', 'Way', 'Circle'];
    const suggestions = [];
    
    // Check if input looks like a number (start of address)
    const startsWithNumber = /^\d+/.test(trimmedInput);
    
    if (startsWithNumber) {
      // Extract the number and any following text
      const match = trimmedInput.match(/^(\d+)\s*(.*)/);
      if (match) {
        const number = match[1];
        const streetPart = match[2] || '';
        
        // Generate suggestions with different street types
        streetTypes.forEach(type => {
          if (streetPart === '' || type.toLowerCase().includes(streetPart) || streetPart.includes(type.toLowerCase().substring(0, 2))) {
            suggestions.push(`${number} Main ${type}`);
            suggestions.push(`${number} Oak ${type}`);
            suggestions.push(`${number} Park ${type}`);
          }
        });
      }
    } else {
      // If doesn't start with number, suggest adding a number
      const capitalizedInput = input.charAt(0).toUpperCase() + input.slice(1);
      suggestions.push(`123 ${capitalizedInput} Street`);
      suggestions.push(`456 ${capitalizedInput} Avenue`);
      suggestions.push(`789 ${capitalizedInput} Road`);
    }
    
    // Return unique suggestions (max 5)
    return [...new Set(suggestions)].slice(0, 5);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'signal_generator': return <Zap className="w-6 h-6" />;
      case 'coaching': return <Users className="w-6 h-6" />;
      case 'mentorship': return <Target className="w-6 h-6" />;
      case 'indicator': return <BarChart3 className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'signal_generator': return 'from-blue-600 to-cyan-600';
      case 'coaching': return 'from-purple-600 to-pink-600';
      case 'mentorship': return 'from-amber-600 to-orange-600';
      case 'indicator': return 'from-green-600 to-emerald-600';
      default: return 'from-gray-600 to-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-md border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - clickable for secret admin access */}
            <div 
              className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0"
              onClick={handleLogoClick}
            >
              <img 
                src="/logo.png" 
                alt="ForTheWisePro LLC" 
                className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-contain ring-2 ring-amber-400/40"
              />
              <div className="hidden sm:block">
                <h1 className="text-sm sm:text-xl font-bold bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400 bg-clip-text text-transparent tracking-wide">FORTHEWISEPRO</h1>
                <p className="text-xs text-amber-200/60">Trading Education</p>
              </div>
            </div>
            
            {/* Right side - Sign In & Cart */}
            <div className="flex items-center gap-1 sm:gap-3">
              {/* Courses — Trading Mastery Program (e-learning) */}
              <Button
                onClick={() => navigate('/learn')}
                variant="outline"
                size="sm"
                className="border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 text-xs sm:text-sm px-2 sm:px-4 group"
                data-testid="header-courses-btn"
              >
                <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">Courses</span>
              </Button>

              {/* AI Coach — accessible to anyone (login is gated inside /scorecard) */}
              <Button
                onClick={() => navigate('/scorecard')}
                variant="outline"
                size="sm"
                className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10 text-xs sm:text-sm px-2 sm:px-4 group"
                data-testid="header-ai-coach-btn"
              >
                <Brain className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline">AI Coach</span>
              </Button>

              {/* Sign In Button */}
              {user ? (
                <div className="flex items-center gap-1 sm:gap-3">
                  <Button
                    onClick={() => setShowMyServicesModal(true)}
                    variant="outline"
                    size="sm"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <Package className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                    <span className="hidden sm:inline">My Services</span>
                  </Button>
                  <Button
                    onClick={() => {
                      localStorage.removeItem('guestToken');
                      setUser(null);
                      toast.success('Signed out successfully');
                    }}
                    variant="outline"
                    size="sm"
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs sm:text-sm px-2 sm:px-4"
                  >
                    <LogOut className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowSignInModal(true)}
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <Lock className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              )}
              
              {/* Shopping Cart Button */}
              <Button
                onClick={() => setShowCart(true)}
                variant="outline"
                size="sm"
                className="relative border-white/20 text-white hover:bg-white/10 px-2 sm:px-4"
              >
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Welcome Banner for Logged-in Users */}
      {user && (
        <div className="bg-gradient-to-r from-green-600/20 via-emerald-600/20 to-teal-600/20 border-b border-green-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {user.last_name ? user.last_name.charAt(0).toUpperCase() : user.name?.split(' ').pop()?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">
                    Welcome, {user.last_name || user.name?.split(' ').pop() || 'User'}!
                  </p>
                  <p className="text-green-400 text-sm">
                    {user.purchased_products?.length > 0 
                      ? `${user.purchased_products.length} product(s) owned` 
                      : "Time to select your products"}
                  </p>
                </div>
              </div>
              {user.purchased_products?.length === 0 && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  Browse our products below
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Promotion Banner */}
      {promotion && promotion.enabled && (
        <div className={`border-b ${
          promotion.banner_color === 'green' ? 'bg-green-500/20 border-green-500/30' :
          promotion.banner_color === 'blue' ? 'bg-blue-500/20 border-blue-500/30' :
          promotion.banner_color === 'red' ? 'bg-red-500/20 border-red-500/30' :
          promotion.banner_color === 'purple' ? 'bg-purple-500/20 border-purple-500/30' :
          'bg-yellow-500/20 border-yellow-500/30'
        }`}
          data-testid="promotion-banner"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-4 flex-wrap">
            <span className={`text-lg font-bold ${
              promotion.banner_color === 'green' ? 'text-green-400' :
              promotion.banner_color === 'blue' ? 'text-blue-400' :
              promotion.banner_color === 'red' ? 'text-red-400' :
              promotion.banner_color === 'purple' ? 'text-purple-400' :
              'text-yellow-400'
            }`}>🎉</span>
            <span className={`font-medium ${
              promotion.banner_color === 'green' ? 'text-green-300' :
              promotion.banner_color === 'blue' ? 'text-blue-300' :
              promotion.banner_color === 'red' ? 'text-red-300' :
              promotion.banner_color === 'purple' ? 'text-purple-300' :
              'text-yellow-300'
            }`}>
              {promotion.discount_text || 'Special Offer!'}
            </span>
            
            {/* Flash Sale Countdown Timer */}
            {promoCountdown && !promoCountdown.expired && (
              <div className="flex items-center gap-1 px-2 py-1 bg-black/30 rounded-lg">
                <Clock className="w-3 h-3 text-red-400" />
                <span className="text-xs font-mono font-bold text-white">
                  {promoCountdown.days > 0 && `${promoCountdown.days}d `}
                  {String(promoCountdown.hours).padStart(2, '0')}:
                  {String(promoCountdown.minutes).padStart(2, '0')}:
                  {String(promoCountdown.seconds).padStart(2, '0')}
                </span>
              </div>
            )}
            {promoCountdown?.expired && (
              <span className="text-xs text-red-400 px-2 py-1 bg-red-500/20 rounded">
                ⏰ Sale Ended
              </span>
            )}
            
            {promotion.discount_code && (
              <button
                onClick={copyDiscountCode}
                className={`px-3 py-1 rounded-lg border font-mono text-sm transition-colors ${
                  promotion.banner_color === 'green' ? 'border-green-500/50 text-green-300 hover:bg-green-500/20' :
                  promotion.banner_color === 'blue' ? 'border-blue-500/50 text-blue-300 hover:bg-blue-500/20' :
                  promotion.banner_color === 'red' ? 'border-red-500/50 text-red-300 hover:bg-red-500/20' :
                  promotion.banner_color === 'purple' ? 'border-purple-500/50 text-purple-300 hover:bg-purple-500/20' :
                  'border-yellow-500/50 text-yellow-300 hover:bg-yellow-500/20'
                }`}
              >
                Code: {promotion.discount_code} 📋
              </button>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-32">
        {/* Layered gold glow on black */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-yellow-700/10 to-black"></div>
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-amber-500/10 blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNGRkQ3MDAiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMSIvPjwvZz48L2c+PC9zdmc+')] opacity-60"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-400/40 mb-6 tracking-wider uppercase text-[10px] px-3 py-1">
            ✨ Professional Trading Tools
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            Level Up Your
            <span className="bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-500 bg-clip-text text-transparent"> Trading Game</span>
          </h1>
          <p className="text-lg sm:text-xl text-amber-50/70 max-w-3xl mx-auto mb-8">
            A smart trading education environment focused on skills development and trading knowledge.
          </p>
          <Button
            onClick={() => document.getElementById('products').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-300 hover:to-yellow-400 text-black font-bold px-8 py-6 text-lg shadow-[0_0_40px_rgba(251,191,36,0.35)] hover:shadow-[0_0_60px_rgba(251,191,36,0.55)] transition-shadow"
          >
            Browse Products
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our <span className="text-amber-400">Products</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Choose from our range of professional trading tools and educational programs
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <div
                  key={product.product_id}
                  className="group relative bg-zinc-900/70 border border-amber-500/10 rounded-2xl overflow-hidden hover:border-amber-400/50 transition-all duration-300 hover:transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/20"
                >
                  {/* Product Image */}
                  <div 
                    className="relative h-48 overflow-hidden cursor-pointer"
                    onClick={() => handleProductClick(product)}
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                    <div className={`absolute top-4 left-4 p-2 rounded-xl bg-gradient-to-r ${getCategoryColor(product.category)}`}>
                      {getCategoryIcon(product.category)}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-5">
                    <h3 
                      className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors cursor-pointer"
                      onClick={() => handleProductClick(product)}
                    >
                      {product.name}
                    </h3>
                    
                    {/* Product Rating Display */}
                    {productRatings[product.product_id]?.count > 0 && (
                      <button
                        onClick={() => handleShowReviews(product.product_id)}
                        className="flex items-center gap-1.5 mb-3 hover:opacity-80 transition-opacity"
                        data-testid={`product-rating-${product.product_id}`}
                      >
                        <div className="flex items-center gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= Math.round(productRatings[product.product_id].average)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-yellow-400 font-medium text-sm">
                          {productRatings[product.product_id].average}
                        </span>
                        <span className="text-gray-500 text-sm">
                          ({productRatings[product.product_id].count} reviews)
                        </span>
                      </button>
                    )}
                    
                    <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                      {product.short_description}
                    </p>

                    {/* Pricing - Show both options for signal_generator */}
                    <div className="mb-4">
                      {product.category === 'signal_generator' && product.secondary_price ? (
                        <div className="space-y-1">
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-white">${product.price}</span>
                            <span className="text-sm text-gray-500">/week</span>
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-xl font-bold text-green-400">${product.secondary_price}</span>
                            <span className="text-sm text-gray-500">/month</span>
                            <span className="text-xs text-green-500 bg-green-500/10 px-2 py-0.5 rounded">Save 25%</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-white">
                            {product.price === 0 ? 'Custom' : `$${product.price}`}
                          </span>
                          {product.price_type && product.price > 0 && (
                            <span className="text-sm text-gray-500">
                              /{product.price_type === 'weekly' ? 'week' : product.price_type === 'monthly' ? 'month' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Features Preview */}
                    <div className="space-y-1 mb-4">
                      {product.features?.slice(0, 2).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                          <span className="truncate">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleProductClick(product)}
                        variant="ghost"
                        className="w-full text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 border border-blue-500/30"
                        data-testid={`view-details-${product.product_id}`}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            // If product has sub_products, open modal for plan selection
                            if (product.sub_products && product.sub_products.length > 0) {
                              toast.info('Please select a plan first');
                              handleProductClick(product);
                              return;
                            }
                            addToCart(product);
                          }}
                          variant="outline"
                          className="flex-1 border-white/20 text-white hover:bg-white/10"
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            // If product has sub_products, open modal for plan selection
                            if (product.sub_products && product.sub_products.length > 0) {
                              toast.info('Please select a plan first');
                              handleProductClick(product);
                              return;
                            }
                            handleBuyNow(product);
                          }}
                          className={`flex-1 bg-gradient-to-r ${getCategoryColor(product.category)} hover:opacity-90 text-white`}
                        >
                          Buy Now
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black border-y border-amber-500/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-2xl hover:bg-amber-500/[0.03] transition-colors">
              <div className="w-16 h-16 bg-amber-500/15 ring-1 ring-amber-400/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-amber-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Smart Trading Alerts</h3>
              <p className="text-gray-400">Get instant trading alerts powered by advanced technical analysis</p>
            </div>
            <div className="text-center p-6 rounded-2xl hover:bg-amber-500/[0.03] transition-colors">
              <div className="w-16 h-16 bg-yellow-500/15 ring-1 ring-yellow-400/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-yellow-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Professional Mentorship</h3>
              <p className="text-gray-400">Learn from professional traders with personalized coaching</p>
            </div>
            <div className="text-center p-6 rounded-2xl hover:bg-amber-500/[0.03] transition-colors">
              <div className="w-16 h-16 bg-amber-400/15 ring-1 ring-amber-300/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-amber-200" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Premium Indicators</h3>
              <p className="text-gray-400">Custom TradingView indicators for precise entry alerts</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-black via-zinc-950 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">{siteContent.aboutUs.title}</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed">
              {siteContent.aboutUs.content}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent">{siteContent.contactUs.title}</span>
            </h2>
            <p className="text-gray-400">Get in touch with our team</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-zinc-900/70 border border-amber-500/15 hover:border-amber-400/40 rounded-xl p-6 text-center transition-colors">
              <div className="w-12 h-12 bg-amber-500/20 ring-1 ring-amber-400/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-amber-300" />
              </div>
              <h3 className="font-bold text-white mb-2">Email</h3>
              <a href={`mailto:${siteContent.contactUs.email}`} className="text-amber-300 hover:underline">
                {siteContent.contactUs.email}
              </a>
            </div>
            <div className="bg-zinc-900/70 border border-amber-500/15 hover:border-amber-400/40 rounded-xl p-6 text-center transition-colors">
              <div className="w-12 h-12 bg-green-500/20 ring-1 ring-green-400/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="font-bold text-white mb-2">Phone</h3>
              <a href={`tel:${siteContent.contactUs.phone}`} className="text-green-400 hover:underline">
                {siteContent.contactUs.phone}
              </a>
            </div>
            <div className="bg-zinc-900/70 border border-amber-500/15 hover:border-amber-400/40 rounded-xl p-6 text-center transition-colors">
              <div className="w-12 h-12 bg-yellow-500/20 ring-1 ring-yellow-400/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-yellow-300" />
              </div>
              <h3 className="font-bold text-white mb-2">Location</h3>
              <p className="text-gray-400">{siteContent.contactUs.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-black border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo.png" 
                alt="ForTheWisePro LLC" 
                className="w-10 h-10 rounded-xl object-contain ring-2 ring-amber-400/40"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-amber-300 to-yellow-400 bg-clip-text text-transparent tracking-wide">FORTHEWISEPRO</span>
            </div>
            
            {/* Footer Links */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => setShowTermsModal(true)}
                className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Service Terms
              </button>
              <button
                onClick={() => setShowRefundModal(true)}
                className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Refund Policy
              </button>
            </div>
            
            <p className="text-gray-500 text-sm">
              © 2025 FORTHEWISEPRO LLC. All rights reserved.
            </p>
          </div>
          
          {/* Privacy & Security Section */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-green-400" />
              <h3 className="text-white font-semibold">Privacy & Security</h3>
            </div>
            <div className="max-w-3xl mx-auto text-center space-y-3">
              <p className="text-gray-400 text-sm">
                We are committed to protecting your personal information. FORTHEWISEPRO LLC does not sell, 
                trade, or rent your personally identifiable information (PII) to third parties.
              </p>
              <p className="text-gray-400 text-sm">
                We collect certain customer information solely to create your account profile and process 
                your transactions securely. All payments are processed by trusted third-party payment 
                processors, and your payment data is handled in accordance with their respective privacy policies.
              </p>
              <p className="text-gray-500 text-xs mt-4">
                By using our services, you consent to the collection and use of information as described above.
              </p>
            </div>
          </div>
          
          <p className="text-center text-gray-600 text-xs mt-6">
            Educational content only - not financial advice.
          </p>
        </div>
      </footer>

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowCart(false)}></div>
          <div className="relative w-full max-w-md bg-slate-900 border-l border-white/10 h-full overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Shopping Cart ({getCartCount()})
                </h2>
                <button
                  onClick={() => setShowCart(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Your cart is empty</p>
                  <Button
                    onClick={() => setShowCart(false)}
                    className="mt-4"
                    variant="outline"
                  >
                    Continue Shopping
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map((item) => (
                      <div key={item.id} className="bg-slate-800/50 border border-white/10 rounded-xl p-4">
                        <div className="flex gap-3">
                          <img
                            src={item.image_url}
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="font-medium text-white text-sm">{item.name}</h3>
                            <p className="text-green-400 font-bold">
                              ${item.price}
                              {item.price_type && <span className="text-xs text-gray-500">/{item.price_type === 'monthly' ? 'mo' : item.price_type === 'weekly' ? 'wk' : ''}</span>}
                            </p>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="p-1 hover:bg-red-500/20 rounded text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateQuantity(item.id, -1)}
                              className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20"
                            >
                              <Minus className="w-4 h-4 text-white" />
                            </button>
                            <span className="text-white w-8 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, 1)}
                              className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg hover:bg-white/20"
                            >
                              <Plus className="w-4 h-4 text-white" />
                            </button>
                          </div>
                          <span className="text-white font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Cart Total */}
                  <div className="border-t border-white/10 pt-4 mb-6">
                    <div className="flex justify-between text-lg font-bold text-white">
                      <span>Total</span>
                      <span>${getCartTotal().toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {/* Secure Payment Notice */}
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 text-green-400 font-medium mb-2">
                      <CheckCircle className="w-5 h-5" />
                      Secure Payment
                    </div>
                    <p className="text-sm text-gray-400">
                      Payments are processed securely through Square.
                    </p>
                  </div>
                  
                  {/* Checkout Button */}
                  <Button
                    onClick={() => {
                      setShowCart(false);
                      setShowCheckout(true);
                    }}
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold py-6"
                  >
                    Proceed to Checkout - ${getCartTotal().toFixed(2)}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl my-8">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">Checkout</h2>
                  {/* Step indicator */}
                  {!user && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${checkoutStep >= 1 ? 'bg-green-500 text-white' : 'bg-slate-700 text-gray-400'}`}>
                        {checkoutAccountCreated ? <CheckCircle className="w-4 h-4" /> : '1'}
                      </div>
                      <div className={`w-12 h-1 ${checkoutStep >= 2 ? 'bg-green-500' : 'bg-slate-700'}`} />
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${checkoutStep >= 2 ? 'bg-green-500 text-white' : 'bg-slate-700 text-gray-400'}`}>
                        2
                      </div>
                      <div className={`w-12 h-1 ${checkoutStep >= 3 ? 'bg-green-500' : 'bg-slate-700'}`} />
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${checkoutStep >= 3 ? 'bg-green-500 text-white' : 'bg-slate-700 text-gray-400'}`}>
                        3
                      </div>
                      <span className="text-xs text-gray-400 ml-2">
                        {checkoutStep === 1 ? 'Create Account' : checkoutStep === 2 ? 'Billing Info' : 'Payment'}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    // Always allow closing with X button
                    setShowCheckout(false);
                    // Only clear state if not on success step
                    if (checkoutStep !== 4) {
                      // Keep the checkout state so they can resume later
                    } else {
                      clearCheckoutState();
                      setCheckoutStep(1);
                    }
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Order Summary */}
              <div className="bg-slate-800/50 border border-white/10 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-white mb-3">Order Summary</h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-400">{item.name} x{item.quantity}</span>
                      <span className="text-white">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/10 mt-3 pt-3 flex justify-between font-bold">
                  <span className="text-white">Total</span>
                  <span className="text-green-400">${getCartTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* STEP 1: Account Creation (for guests only) */}
              {!user && checkoutStep === 1 && (
                <div className="space-y-4">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                    <h3 className="font-bold text-blue-400 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Create Your Account
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Create an account to securely complete your purchase and access your services.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={checkoutAccountForm.firstName}
                        onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, firstName: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={checkoutAccountForm.lastName}
                        onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, lastName: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email Address *</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={checkoutAccountForm.email}
                        onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, email: e.target.value})}
                        disabled={checkoutVerificationSent}
                        className="flex-1 bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none disabled:opacity-50"
                        placeholder="john@example.com"
                      />
                      {!checkoutVerified && (
                        <Button
                          onClick={handleSendCheckoutVerification}
                          disabled={sendingCheckoutVerification || !checkoutAccountForm.email || checkoutVerificationSent}
                          className="bg-blue-600 hover:bg-blue-500 whitespace-nowrap"
                        >
                          {sendingCheckoutVerification ? 'Sending...' : checkoutVerificationSent ? 'Sent!' : 'Verify Email'}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Verification Code Input */}
                  {checkoutVerificationSent && !checkoutVerified && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                      <label className="block text-sm text-amber-400 font-medium mb-2">
                        Enter 6-digit verification code sent to your email
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={checkoutVerificationCode}
                          onChange={(e) => setCheckoutVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="flex-1 bg-slate-800 border border-amber-500/30 rounded-lg px-4 py-3 text-white text-center text-xl tracking-widest focus:border-amber-500 focus:outline-none"
                          placeholder="000000"
                          maxLength={6}
                        />
                        <Button
                          onClick={handleVerifyCheckoutCode}
                          disabled={verifyingCheckoutCode || checkoutVerificationCode.length !== 6}
                          className="bg-amber-600 hover:bg-amber-500"
                        >
                          {verifyingCheckoutCode ? 'Verifying...' : 'Verify'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Email Verified - Show password fields */}
                  {checkoutVerified && (
                    <>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-medium">Email verified!</span>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          value={checkoutAccountForm.phone}
                          onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, phone: e.target.value})}
                          className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      
                      <h4 className="font-bold text-white mt-4">Mailing Address</h4>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Street Address *</label>
                        <input
                          type="text"
                          value={checkoutAccountForm.mailingAddress}
                          onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingAddress: e.target.value})}
                          className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                          placeholder="123 Main Street"
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">City *</label>
                          <input
                            type="text"
                            value={checkoutAccountForm.mailingCity}
                            onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingCity: e.target.value})}
                            className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                            placeholder="City"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">ZIP Code *</label>
                          <input
                            type="text"
                            value={checkoutAccountForm.mailingZipCode}
                            onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingZipCode: e.target.value})}
                            className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                            placeholder="12345"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Country *</label>
                          <select
                            value={checkoutAccountForm.mailingCountry}
                            onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingCountry: e.target.value, mailingState: ''})}
                            className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Select Country</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>{country}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">State/Province</label>
                          {getStatesForCountry(checkoutAccountForm.mailingCountry).length > 0 ? (
                            <select
                              value={checkoutAccountForm.mailingState}
                              onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingState: e.target.value})}
                              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                            >
                              <option value="">Select State</option>
                              {getStatesForCountry(checkoutAccountForm.mailingCountry).map((state) => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={checkoutAccountForm.mailingState}
                              onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, mailingState: e.target.value})}
                              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                              placeholder="State (optional)"
                            />
                          )}
                        </div>
                      </div>
                      
                      <h4 className="font-bold text-white mt-4">Create Password</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Password *</label>
                          <input
                            type="password"
                            value={checkoutAccountForm.password}
                            onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, password: e.target.value})}
                            className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                            placeholder="••••••••"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Confirm Password *</label>
                          <input
                            type="password"
                            value={checkoutAccountForm.confirmPassword}
                            onChange={(e) => setCheckoutAccountForm({...checkoutAccountForm, confirmPassword: e.target.value})}
                            className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                            placeholder="••••••••"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Password must be at least 8 characters with one uppercase letter and one special character.
                      </p>
                      
                      <Button
                        onClick={handleCreateCheckoutAccount}
                        disabled={creatingCheckoutAccount}
                        className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4 text-lg font-bold"
                      >
                        {creatingCheckoutAccount ? 'Creating Account...' : 'Create Account & Continue'}
                      </Button>
                    </>
                  )}
                  
                  {/* Already have account? Sign in */}
                  <div className="text-center pt-4 border-t border-white/10 mt-4">
                    <span className="text-gray-400">Already have an account? </span>
                    <button
                      onClick={() => {
                        setShowCheckout(false);
                        setShowSignInModal(true);
                      }}
                      className="text-blue-400 hover:underline font-medium"
                    >
                      Sign In
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Billing Information (for logged in users or after account creation) */}
              {(user || checkoutStep >= 2) && checkoutStep !== 3 && (
                <div className="space-y-4">
                  <h3 className="font-bold text-white">Billing Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={checkoutForm.fullName}
                        onChange={(e) => setCheckoutForm({...checkoutForm, fullName: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={checkoutForm.email}
                        onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      value={checkoutForm.phone}
                      onChange={(e) => setCheckoutForm({...checkoutForm, phone: e.target.value})}
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  {/* TradingView Username - Only show if cart has indicators */}
                  {hasTradingViewIndicators() && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                      <label className="block text-sm text-green-400 font-medium mb-2">
                        <BarChart3 className="w-4 h-4 inline mr-2" />
                        TradingView Email or Username *
                      </label>
                      <input
                        type="text"
                        value={tradingViewUsername}
                        onChange={(e) => setTradingViewUsername(e.target.value)}
                        className="w-full bg-slate-800 border border-green-500/30 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:outline-none"
                        placeholder="Your TradingView username or email"
                      />
                      <p className="text-xs text-gray-400 mt-2">
                        Required for TradingView indicator access. Make sure this matches your TradingView account.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Street Address *</label>
                    <input
                      type="text"
                      value={checkoutForm.address}
                      onChange={(e) => setCheckoutForm({...checkoutForm, address: e.target.value})}
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">City *</label>
                      <input
                        type="text"
                        value={checkoutForm.city}
                        onChange={(e) => setCheckoutForm({...checkoutForm, city: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">ZIP/Postal Code *</label>
                      <input
                        type="text"
                        value={checkoutForm.zipCode}
                        onChange={(e) => setCheckoutForm({...checkoutForm, zipCode: e.target.value})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Country *</label>
                      <select
                        value={checkoutForm.country}
                        onChange={(e) => setCheckoutForm({...checkoutForm, country: e.target.value, state: ''})}
                        className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select Country</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">
                        State/Province {getStatesForCountry(checkoutForm.country).length > 0 ? '*' : ''}
                      </label>
                      {getStatesForCountry(checkoutForm.country).length > 0 ? (
                        <select
                          value={checkoutForm.state}
                          onChange={(e) => setCheckoutForm({...checkoutForm, state: e.target.value})}
                          className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">Select State/Province</option>
                          {getStatesForCountry(checkoutForm.country).map((state) => (
                            <option key={state} value={state}>{state}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={checkoutForm.state}
                          onChange={(e) => setCheckoutForm({...checkoutForm, state: e.target.value})}
                          className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                          placeholder="State/Province (optional)"
                        />
                      )}
                    </div>
                  </div>

                  {/* Payment Info Notice */}
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 mt-6">
                    <div className="flex items-center gap-2 text-green-400 font-medium mb-2">
                      <CheckCircle className="w-5 h-5" />
                      Secure Payment via Square
                    </div>
                    <p className="text-sm text-gray-400">
                      You will be redirected to Square's secure payment page to complete your purchase.
                      After payment, you'll be redirected back here to activate your service.
                    </p>
                  </div>

                  {/* Terms Agreement Checkbox */}
                  <div className="mt-6 p-4 bg-slate-800/50 border border-white/10 rounded-xl">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-white/20 bg-slate-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                      />
                      <span className="text-sm text-gray-300">
                        By proceeding to checkout, I agree to the{' '}
                        <button 
                          type="button"
                          onClick={(e) => { e.preventDefault(); setShowCheckout(false); setShowRefundModal(true); }} 
                          className="text-blue-400 hover:underline font-medium"
                        >
                          Refund Policy
                        </button>{' '}
                        and the{' '}
                        <button 
                          type="button"
                          onClick={(e) => { e.preventDefault(); setShowCheckout(false); setShowTermsModal(true); }} 
                          className="text-blue-400 hover:underline font-medium"
                        >
                          Service Terms
                        </button>.
                      </span>
                    </label>
                  </div>

                  {/* Submit Button - Move to Step 3 */}
                  <Button
                    onClick={async () => {
                      // Validate terms agreement
                      if (!agreedToTerms) {
                        toast.error('Please agree to the Refund Policy and Service Terms to continue');
                        return;
                      }
                      // Validate form
                      if (!checkoutForm.fullName || !checkoutForm.email || !checkoutForm.phone || 
                          !checkoutForm.address || !checkoutForm.city || !checkoutForm.zipCode || !checkoutForm.country) {
                        toast.error('Please fill in all required fields');
                        return;
                      }
                      // Validate state if country has states
                      if (getStatesForCountry(checkoutForm.country).length > 0 && !checkoutForm.state) {
                        toast.error('Please select your state/province');
                        return;
                      }
                      // Validate TradingView username if indicators in cart
                      if (hasTradingViewIndicators() && !tradingViewUsername.trim()) {
                        toast.error('Please enter your TradingView email or username for indicator access');
                        return;
                      }
                      
                      // Save order details to localStorage
                      const orderId = `ORD-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
                      const pendingOrder = {
                        order_id: orderId,
                        email: checkoutForm.email,
                        full_name: checkoutForm.fullName,
                        phone: checkoutForm.phone,
                        billing_address: {
                          street: checkoutForm.address,
                          city: checkoutForm.city,
                          state: checkoutForm.state,
                          zip_code: checkoutForm.zipCode,
                          country: checkoutForm.country
                        },
                        cart_items: cart.map(item => ({
                          id: item.id,
                          product_id: item.product_id,
                          name: item.name,
                          price: item.price,
                          quantity: item.quantity,
                          category: item.category
                        })),
                        total_amount: getCartTotal(),
                        trading_view_username: tradingViewUsername,
                        created_at: new Date().toISOString()
                      };
                      
                      localStorage.setItem('pending_order', JSON.stringify(pendingOrder));
                      
                      // Move to step 3 (Payment)
                      setCheckoutStep(3);
                      saveCheckoutState({ checkoutAccountCreated: true, checkoutStep: 3 });
                      toast.success('Billing info saved! Now proceed to payment.');
                    }}
                    disabled={!agreedToTerms || submittingOrder}
                    className={`w-full mt-6 text-white py-6 text-lg font-bold ${
                      agreedToTerms && !submittingOrder
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold' 
                        : 'bg-gray-600 cursor-not-allowed'
                    }`}
                  >
                    Continue to Payment
                  </Button>
                </div>
              )}

              {/* STEP 3: Payment - Two buttons */}
              {checkoutStep === 3 && (
                <div className="space-y-4">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                    <h3 className="font-bold text-green-400 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Ready for Payment
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      Your billing information has been saved. Choose an option below to complete your purchase.
                    </p>
                  </div>

                  {/* Order Details */}
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <h4 className="text-white font-medium mb-2">Order Details</h4>
                    <div className="text-sm text-gray-400 space-y-1">
                      <p><span className="text-gray-500">Name:</span> {checkoutForm.fullName || 'N/A'}</p>
                      <p><span className="text-gray-500">Email:</span> {checkoutForm.email || 'N/A'}</p>
                      <p><span className="text-gray-500">Total:</span> <span className="text-green-400 font-bold">${getCartTotal().toFixed(2)}</span></p>
                    </div>
                  </div>

                  {/* Option 1: Continue to Square Payment */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <h4 className="text-blue-400 font-bold mb-2">Option 1: Pay Now</h4>
                    <p className="text-sm text-gray-400 mb-3">
                      Click below to make your payment securely via Square. After payment, return here and click "Done with Payment".
                    </p>
                    <Button
                      onClick={() => {
                        // Get payment link from cart items
                        const cartPaymentLinks = cart.filter(item => item.payment_link).map(item => item.payment_link);
                        const paymentLink = cartPaymentLinks.length > 0 
                          ? cartPaymentLinks[0] 
                          : 'https://square.link/u/pcgnBKgL';
                        
                        if (!paymentLink) {
                          toast.error('Payment link not configured. Please contact support.');
                          return;
                        }
                        
                        toast.info('Redirecting to Square payment...');
                        setTimeout(() => {
                          window.location.href = paymentLink;
                        }, 500);
                      }}
                      className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold py-4 text-lg font-bold"
                    >
                      Continue to Square Payment →
                    </Button>
                  </div>

                  {/* Option 2: Done with Payment */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <h4 className="text-green-400 font-bold mb-2">Already Paid?</h4>
                    <p className="text-sm text-gray-400 mb-3">
                      If you've completed your payment on Square, click below to complete your purchase. You can view your service in the Services tab.
                    </p>
                    <Button
                      onClick={async () => {
                        setSubmittingOrder(true);
                        try {
                          const pendingOrder = JSON.parse(localStorage.getItem('pending_order') || '{}');
                          const token = localStorage.getItem('guestToken');
                          
                          // Make sure we have required data
                          if (!pendingOrder.email && !checkoutForm.email) {
                            toast.error('Missing order information. Please try again.');
                            setSubmittingOrder(false);
                            return;
                          }
                          
                          const orderData = {
                            email: pendingOrder.email || checkoutForm.email,
                            order_id: pendingOrder.order_id || `ORD-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
                            cart_items: pendingOrder.cart_items || [],
                            total_amount: pendingOrder.total_amount || 0,
                            full_name: pendingOrder.full_name || checkoutForm.fullName,
                            phone: pendingOrder.phone || checkoutForm.phone,
                            billing_address: pendingOrder.billing_address || null,
                            trading_view_username: pendingOrder.trading_view_username || tradingViewUsername,
                            user_token: token || null
                          };
                          
                          const res = await fetch(`${API}/payment/confirm`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(orderData)
                          });
                          
                          if (!res.ok) {
                            throw new Error(`HTTP error! status: ${res.status}`);
                          }
                          
                          const data = await res.json();
                          
                          if (data.success) {
                            // Clear cart but keep checkout modal open to show success
                            saveCart([]);
                            localStorage.removeItem('pending_order');
                            
                            // Show success state in the checkout
                            setCheckoutStep(4); // Success step
                            toast.success('Purchase complete! Welcome email sent.');
                          } else {
                            toast.error(data.message || 'Failed to confirm payment');
                          }
                        } catch (err) {
                          console.error('Payment confirm error:', err);
                          toast.error('Connection error. Please check your internet and try again.');
                        } finally {
                          setSubmittingOrder(false);
                        }
                      }}
                      disabled={submittingOrder}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4 text-lg font-bold"
                    >
                      {submittingOrder ? 'Completing Purchase...' : '✓ Done with Payment'}
                    </Button>
                  </div>

                  {/* Back button */}
                  <button
                    onClick={() => setCheckoutStep(2)}
                    className="w-full text-gray-400 hover:text-white text-sm py-2"
                  >
                    ← Back to Billing Information
                  </button>
                </div>
              )}

              {/* STEP 4: Success - Purchase Complete */}
              {checkoutStep === 4 && (
                <div className="space-y-4 text-center">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Purchase Complete!</h3>
                  <p className="text-gray-400">
                    Thank you for your purchase. A welcome email has been sent to your inbox.
                  </p>
                  
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mt-4">
                    <p className="text-green-400 font-medium mb-2">What's Next?</p>
                    <p className="text-sm text-gray-400">
                      Your service is now pending approval. You can view it in the <strong className="text-white">Services</strong> tab once activated.
                    </p>
                  </div>

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <p className="text-blue-400 font-medium mb-2">Check Your Email</p>
                    <p className="text-sm text-gray-400">
                      We've sent you a welcome email with instructions on how to get started.
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      clearCheckoutState();
                      setShowCheckout(false);
                      setCheckoutStep(1);
                    }}
                    className="w-full mt-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-4"
                  >
                    Close & View Services
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-xl object-contain" />
                  <h2 className="text-xl font-bold text-white">Sign In</h2>
                </div>
                <button
                  onClick={() => setShowSignInModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={signInForm.email}
                    onChange={(e) => setSignInForm({...signInForm, email: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm text-gray-400">Password</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSignInModal(false);
                        setForgotPasswordEmail(signInForm.email);
                        setShowForgotPasswordModal(true);
                      }}
                      className="text-xs text-blue-400 hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <input
                    type="password"
                    value={signInForm.password}
                    onChange={(e) => setSignInForm({...signInForm, password: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="••••••••"
                    required
                  />
                </div>

                {/* reCAPTCHA Human Verification */}
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={signInCaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={(token) => setSignInCaptchaToken(token)}
                    onExpired={() => setSignInCaptchaToken(null)}
                    theme="dark"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={signInLoading || !signInCaptchaToken}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold py-6"
                >
                  {signInLoading ? 'Signing In...' : 'Sign In to Dashboard'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-gray-500 text-sm">
                  Don&apos;t have an account?{' '}
                  <button
                    onClick={() => {
                      setShowSignInModal(false);
                      setShowCreateAccountModal(true);
                    }}
                    className="text-blue-400 hover:underline"
                  >
                    Create Account
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Lock className="w-8 h-8 text-amber-400" />
                  <h2 className="text-xl font-bold text-white">Forgot Password</h2>
                </div>
                <button
                  onClick={() => setShowForgotPasswordModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <p className="text-gray-400 text-sm mb-6">
                Enter your email address and we&apos;ll send you a code to reset your password.
              </p>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={forgotPasswordLoading}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white py-6"
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    setShowSignInModal(true);
                  }}
                  className="text-gray-500 text-sm hover:text-white"
                >
                  ← Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Lock className="w-8 h-8 text-green-400" />
                  <h2 className="text-xl font-bold text-white">Reset Password</h2>
                </div>
                <button
                  onClick={() => setShowResetPasswordModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300">
                  We sent a 6-digit code to:
                </p>
                <p className="text-blue-400 font-medium mt-1">{forgotPasswordEmail}</p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Reset Code</label>
                  <input
                    type="text"
                    value={resetPasswordCode}
                    onChange={(e) => setResetPasswordCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest focus:border-blue-500 focus:outline-none"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Enter new password"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Min 8 characters, 1 uppercase, 1 special character
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Re-enter password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={resettingPassword || resetPasswordCode.length !== 6}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-6"
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>

              <p className="text-gray-500 text-sm text-center mt-4">
                Didn&apos;t receive the code? Check your spam folder.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-xl object-contain" />
                  <h2 className="text-xl font-bold text-white">Create Account</h2>
                </div>
                <button
                  onClick={() => setShowCreateAccountModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">First Name</label>
                    <input
                      type="text"
                      value={createAccountForm.firstName}
                      onChange={(e) => setCreateAccountForm({...createAccountForm, firstName: e.target.value})}
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="John"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={createAccountForm.lastName}
                      onChange={(e) => setCreateAccountForm({...createAccountForm, lastName: e.target.value})}
                      className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Doe"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={createAccountForm.email}
                    onChange={(e) => setCreateAccountForm({...createAccountForm, email: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Password</label>
                  <input
                    type="password"
                    value={createAccountForm.password}
                    onChange={(e) => setCreateAccountForm({...createAccountForm, password: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Create a strong password"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Min 8 characters, 1 uppercase, 1 special character (!@#$%^&*...)
                  </p>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    value={createAccountForm.confirmPassword}
                    onChange={(e) => setCreateAccountForm({...createAccountForm, confirmPassword: e.target.value})}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:border-blue-500 focus:outline-none"
                    placeholder="Re-enter password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={createAccountLoading}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white py-6"
                >
                  {createAccountLoading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <p className="text-gray-500 text-sm">
                  Already have an account?{' '}
                  <button
                    onClick={() => {
                      setShowCreateAccountModal(false);
                      setShowSignInModal(true);
                    }}
                    className="text-blue-400 hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-8 h-8 text-blue-400" />
                  <h2 className="text-xl font-bold text-white">Verify Your Email</h2>
                </div>
                <button
                  onClick={() => setShowVerificationModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-300">
                  We sent a 6-digit verification code to:
                </p>
                <p className="text-blue-400 font-medium mt-1">{verificationEmail}</p>
              </div>

              <form onSubmit={handleVerifyEmail} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Verification Code</label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest focus:border-blue-500 focus:outline-none"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={verifyingEmail || verificationCode.length !== 6}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold py-6"
                >
                  {verifyingEmail ? 'Verifying...' : 'Verify Email'}
                </Button>
              </form>

              <p className="text-gray-500 text-sm text-center mt-4">
                Didn&apos;t receive the code? Check your spam folder.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Order Success Modal */}
      {showOrderSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-md p-8 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Order Submitted!</h2>
            
            <p className="text-gray-300 mb-6">
              Thank you for your order! We&apos;ve sent an email to:
            </p>
            
            <div className="bg-slate-800 rounded-lg px-4 py-3 mb-6">
              <p className="text-blue-400 font-medium">{orderEmail}</p>
            </div>
            
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 text-amber-400 font-medium mb-2">
                <Mail className="w-5 h-5" />
                Check Your Email
              </div>
              <p className="text-sm text-gray-300">
                Please check your inbox (and spam folder) for instructions to 
                <strong className="text-white"> create your account</strong> and 
                access your purchased products.
              </p>
            </div>
            
            <p className="text-gray-500 text-sm mb-6">
              The email contains a secure link to set up your profile with your 
              name, phone, age, and password.
            </p>
            
            <Button
              onClick={() => setShowOrderSuccess(false)}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold"
            >
              Got It
            </Button>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="relative h-56 overflow-hidden">
              <img
                src={selectedProduct.image_url}
                alt={selectedProduct.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
              <button
                onClick={() => setShowProductModal(false)}
                className="absolute top-4 right-4 p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <div className="absolute bottom-4 left-4 right-4">
                <Badge className={`bg-gradient-to-r ${getCategoryColor(selectedProduct.category)} text-white mb-2`}>
                  {selectedProduct.category.replace('_', ' ').toUpperCase()}
                </Badge>
                <h2 className="text-2xl font-bold text-white">{selectedProduct.name}</h2>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Description with markdown-style formatting */}
              <div className="text-gray-300 mb-6 whitespace-pre-line">
                {selectedProduct.full_description?.split('\n').map((line, idx) => {
                  if (line.startsWith('**') && line.endsWith('**')) {
                    return <h4 key={idx} className="font-bold text-white mt-4 mb-2">{line.replace(/\*\*/g, '')}</h4>;
                  }
                  if (line.startsWith('•')) {
                    return <p key={idx} className="ml-4 text-gray-400">{line}</p>;
                  }
                  if (line.includes('DISCLAIMER') || line.includes('IMPORTANT')) {
                    return <p key={idx} className="text-amber-400 font-medium mt-4">{line.replace(/\*\*/g, '')}</p>;
                  }
                  return <p key={idx}>{line}</p>;
                })}
              </div>

              {/* Sub-products for indicators */}
              {selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-white mb-4">Choose Your Option</h3>
                  <div className="space-y-3">
                    {selectedProduct.sub_products.map((subProduct) => (
                      <div
                        key={subProduct.sub_id}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          selectedSubProduct?.sub_id === subProduct.sub_id
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-white/10 bg-white/5 hover:border-white/20'
                        }`}
                        onClick={() => setSelectedSubProduct(subProduct)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-bold text-white">{subProduct.name}</h4>
                          <span className="text-green-400 font-bold">
                            ${subProduct.price}/{subProduct.price_type === 'monthly' ? 'mo' : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{subProduct.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {subProduct.features?.map((f, i) => (
                            <Badge key={i} variant="outline" className="text-xs border-white/20 text-gray-400">
                              {f}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-white mb-4">What&apos;s Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {selectedProduct.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing Card */}
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-gray-400 text-sm">Price</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black text-white">
                        ${selectedSubProduct ? selectedSubProduct.price : selectedProduct.price}
                      </span>
                      <span className="text-gray-400">
                        /{selectedSubProduct?.price_type || selectedProduct.price_type === 'weekly' ? 'week' : selectedProduct.price_type === 'monthly' ? 'month' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Secure Payment Notice */}
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">Secure payment via Square</span>
                  </div>
                </div>
              </div>

              {/* Plan Selection Required Warning */}
              {selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">Please select a plan above to continue</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => {
                    // Check if plan selection is required
                    if (selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct) {
                      toast.warning('Please select a plan first before adding to cart');
                      return;
                    }
                    addToCart(selectedProduct, selectedSubProduct);
                    setShowProductModal(false);
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10 py-6"
                  disabled={selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct}
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Add to Cart
                </Button>
                <Button
                  onClick={() => {
                    // Check if plan selection is required
                    if (selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct) {
                      toast.warning('Please select a plan first before purchasing');
                      return;
                    }
                    handleBuyNow(selectedProduct, selectedSubProduct);
                  }}
                  className={`flex-1 text-white py-6 ${
                    selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold'
                  }`}
                  disabled={selectedProduct.sub_products && selectedProduct.sub_products.length > 0 && !selectedSubProduct}
                >
                  Buy Now
                </Button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-gray-500 text-center mt-4">
                Educational content only. Not financial advice.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Service Terms Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Service Terms
                </h2>
                <button
                  onClick={() => setShowTermsModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-gray-300 whitespace-pre-wrap">{siteContent.serviceTerms}</p>
              <Button
                onClick={() => setShowTermsModal(false)}
                className="w-full mt-6"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Policy Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Refund Policy
                </h2>
                <button
                  onClick={() => setShowRefundModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-gray-300 whitespace-pre-wrap">{siteContent.refundPolicy}</p>
              <Button
                onClick={() => setShowRefundModal(false)}
                className="w-full mt-6"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* My Services Modal */}
      {showMyServicesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-green-400" />
                  <h2 className="text-xl font-bold text-white">My Services</h2>
                </div>
                <button
                  onClick={() => setShowMyServicesModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* User Info */}
              <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                <p className="text-white font-medium">{user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User'}</p>
                <p className="text-sm text-gray-400">{user?.email}</p>
              </div>

              {/* Services List */}
              {user?.purchased_products && user.purchased_products.length > 0 ? (
                <div className="space-y-3 mb-6">
                  <p className="text-sm text-gray-400 mb-2">Your Active Services:</p>
                  {user.purchased_products.map((productId, index) => {
                    // Check if this is a Bonbot product (signal generator)
                    const isBonbot = productId?.toLowerCase().includes('bonbot') || 
                                     productId?.toLowerCase().includes('signal');
                    const productName = products.find(p => p.product_id === productId)?.name || productId;
                    
                    return isBonbot ? (
                      <div 
                        key={index}
                        data-testid="bonbot-service-card"
                        onClick={() => navigate('/portal')}
                        className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-green-500/20 hover:border-green-400/50 transition-all group"
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <div>
                            <span className="text-white font-medium">{productName}</span>
                            <p className="text-xs text-green-400 group-hover:text-green-300">Click to access Signal Generator →</p>
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      </div>
                    ) : (
                      <div key={index} className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="text-white">{productName}</span>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400">Active</Badge>
                      </div>
                    );
                  })}
                  
                  {/* PRO Upgrade Banner for non-PRO users */}
                  {!user?.has_pro_access && (
                    <div 
                      onClick={() => {
                        setShowMyServicesModal(false);
                        setShowProPromo(true);
                      }}
                      className="bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border border-yellow-500/30 rounded-lg p-4 cursor-pointer hover:from-yellow-500/30 hover:via-orange-500/30 hover:to-red-500/30 transition-all group"
                      data-testid="pro-upgrade-banner-with-products"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                            <Crown className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Upgrade to PRO Scalper</p>
                            <p className="text-xs text-yellow-300/80">Advanced signals • Higher win rate</p>
                          </div>
                        </div>
                        <button className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold rounded-lg">
                          Watch Demo
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 mb-6">
                  <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="text-gray-400 mb-2">No services linked yet</p>
                  <p className="text-sm text-gray-500">
                    Purchase a product or link your existing purchase below
                  </p>
                </div>
              )}
              
              {/* PRO Upgrade Banner - Show outside of products list for users without products */}
              {(!user?.purchased_products || user.purchased_products.length === 0) && !user?.has_pro_access && (
                <div 
                  onClick={() => {
                    setShowMyServicesModal(false);
                    setShowProPromo(true);
                  }}
                  className="bg-gradient-to-r from-yellow-500/20 via-orange-500/20 to-red-500/20 border border-yellow-500/30 rounded-lg p-4 cursor-pointer hover:from-yellow-500/30 hover:via-orange-500/30 hover:to-red-500/30 transition-all group mb-6"
                  data-testid="pro-upgrade-banner-no-products"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">Upgrade to PRO Scalper</p>
                        <p className="text-xs text-yellow-300/80">Advanced signals • Higher win rate</p>
                      </div>
                    </div>
                    <button className="px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-xs font-bold rounded-lg">
                      Watch Demo
                    </button>
                  </div>
                </div>
              )}

              {/* Link My Service Button */}
              <div className="border-t border-white/10 pt-6">
                <Button
                  onClick={() => {
                    toast.info(
                      <div className="text-left">
                        <p className="font-bold text-lg text-slate-900 mb-2">Check Your Email</p>
                        <p className="text-sm text-slate-700">
                          If you&apos;ve made a purchase, check your email for the activation link to link your service to your profile.
                        </p>
                      </div>,
                      { duration: 8000 }
                    );
                  }}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-semibold py-6"
                >
                  <Link2 className="w-5 h-5 mr-2" />
                  Link My Service
                </Button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  After purchasing, you&apos;ll receive an email with a link to activate your service
                </p>
              </div>

              {/* Browse Products */}
              <div className="mt-4">
                <Button
                  onClick={() => {
                    setShowMyServicesModal(false);
                    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Browse Products
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Reviews Modal */}
      {showReviewsModal && selectedProductReviews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Customer Reviews</h2>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= Math.round(selectedProductReviews.average)
                                ? 'fill-white text-white'
                                : 'text-white/40'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-white font-medium">{selectedProductReviews.average}</span>
                      <span className="text-white/80 text-sm">({selectedProductReviews.count} reviews)</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowReviewsModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="close-reviews-modal"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            {/* Rating Distribution */}
            {selectedProductReviews.distribution && (
              <div className="px-6 py-4 border-b border-white/10 bg-slate-800/50">
                <div className="space-y-2">
                  {[5, 4, 3, 2, 1].map((num) => {
                    const count = selectedProductReviews.distribution[num.toString()] || 0;
                    const percentage = selectedProductReviews.count > 0 
                      ? Math.round((count / selectedProductReviews.count) * 100) 
                      : 0;
                    return (
                      <div key={num} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-400 w-3">{num}</span>
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-400 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-gray-500 w-8 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Reviews List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {selectedProductReviews.reviews && selectedProductReviews.reviews.length > 0 ? (
                selectedProductReviews.reviews.map((review, idx) => (
                  <div 
                    key={idx}
                    className="bg-slate-800/50 border border-white/10 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {review.user_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <span className="text-white font-medium text-sm">{review.user_name}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-3 h-3 ${
                              star <= review.rating
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-600'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-300 text-sm">{review.review_text}</p>
                    {review.approved_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(review.approved_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Star className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-400">No reviews yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PRO Upgrade Promo Slideshow */}
      <ProUpgradePromo
        isOpen={showProPromo}
        onClose={() => setShowProPromo(false)}
        onUpgrade={() => {
          setShowProPromo(false);
          setShowUpgradeModal(true);
        }}
      />
      
      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        user={user}
        onUpgradeSuccess={() => {
          // Refresh user data
          if (user?.email) {
            fetch(`${API}/guest/status?email=${encodeURIComponent(user.email)}`)
              .then(res => res.json())
              .then(data => {
                if (data.user) {
                  setUser(prev => ({ ...prev, ...data.user }));
                }
              });
          }
        }}
      />

      {/* AI Sales Assistant chat bubble — available to everyone (signed-in or guest) */}
      <SalesChatBubble />
    </div>
  );
};

export default StoreLanding;
